import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Card, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { installationEntfernen, installationenListe } from '../api';
import { Ampel, Bestaetigung, Seitenkopf, StatusPille } from '../komponenten/Bausteine';
import { fehlertext, MODUS, seit, zeitpunkt } from '../utils/format';

const FILTER = [
  ['alle', 'Alle', () => true],
  ['veraltet', 'Veraltet', (i) => i.veraltet],
  ['still', 'Melden sich nicht', (i) => i.ampel !== 'gruen'],
  ['test', 'Testphase', (i) => !i.lizenz_id && i.letzter_status === 'testphase'],
  ['fremd', 'Falsches Gerät', (i) => i.letzter_status === 'hardware_mismatch'],
];

const anteil = (belegt, max) => (belegt == null ? (max == null ? '–' : `? / ${max}`) : `${belegt} / ${max ?? '?'}`);

export default function Installationen() {
  const navigate = useNavigate();
  const [liste, setListe] = useState(null);
  const [fehler, setFehler] = useState('');
  const [suche, setSuche] = useState('');
  const [filter, setFilter] = useState('alle');
  const [weg, setWeg] = useState(null);

  const laden = useCallback(() => {
    installationenListe().then((r) => setListe(r.data)).catch((e) => setFehler(fehlertext(e, 'Installationen konnten nicht geladen werden.')));
  }, []);
  useEffect(laden, [laden]);

  const sichtbar = useMemo(() => {
    const worte = suche.toLowerCase().split(/\s+/).filter(Boolean);
    const passt = FILTER.find((f) => f[0] === filter)[2];
    return (liste || []).filter((i) => {
      if (!passt(i)) return false;
      const heu = [i.kunde_name, i.firma, i.aktuelle_version, i.hardware_id, i.ip_adresse].filter(Boolean).join(' ').toLowerCase();
      return worte.every((w) => heu.includes(w));
    });
  }, [liste, suche, filter]);

  return (
    <Box>
      <Seitenkopf titel="Installationen" untertitel="Jede ProfiWare-Installation meldet sich stündlich. Nach 3 Tagen ohne Meldung sperrt sie sich selbst." />
      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField
          id="installationen-suche" placeholder="Kunde, Version oder Hardware-ID suchen" value={suche}
          onChange={(e) => setSuche(e.target.value)} sx={{ flex: '1 1 260px', bgcolor: 'background.paper' }}
        />
        {FILTER.map(([id, text, passt]) => (
          <Chip
            key={id} label={`${text} ${(liste || []).filter(passt).length}`} onClick={() => setFilter(id)}
            color={filter === id ? 'primary' : 'default'} variant={filter === id ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kunde</TableCell>
                <TableCell>Zuletzt gemeldet</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Arbeitsplätze</TableCell>
                <TableCell>Mandanten</TableCell>
                <TableCell>Lizenz</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sichtbar.map((i) => (
                <TableRow
                  key={i.id} hover={!!i.kunde_id} sx={{ cursor: i.kunde_id ? 'pointer' : 'default' }}
                  onClick={() => i.kunde_id && navigate(`/kunden/${i.kunde_id}`)}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>{i.kunde_name || i.firma || 'Unbekannt'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[i.kunde_name && i.firma && i.firma !== i.kunde_name ? i.firma : null, MODUS[i.modus] || i.modus, i.ip_adresse].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell title={zeitpunkt(i.letzter_kontakt)}><Ampel farbe={i.ampel}>{seit(i.letzter_kontakt)}</Ampel></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {i.aktuelle_version || '–'} {i.veraltet && <Chip size="small" color="warning" label="veraltet" />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{i.lizenz_id ? anteil(i.belegte_arbeitsplaetze, i.max_arbeitsplaetze) : '–'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{i.lizenz_id ? anteil(i.angelegte_mandanten, i.max_mandanten) : '–'}</TableCell>
                  <TableCell>
                    {i.letzter_status === 'hardware_mismatch'
                      ? <Chip size="small" color="error" label="Falsches Gerät" />
                      : i.lizenz_id
                        ? <StatusPille status={i.lizenz_status} tage={i.tage_bis_ablauf} />
                        : <Chip size="small" variant="outlined" label={i.letzter_status === 'testphase' ? 'Testphase' : 'Ohne Lizenz'} />}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Aus der Liste entfernen">
                      <IconButton size="small" aria-label="Aus der Liste entfernen" onClick={(e) => { e.stopPropagation(); setWeg(i); }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {liste && sichtbar.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    {liste.length === 0 ? 'Noch hat sich keine Installation gemeldet.' : 'Keine Installation passt zu Suche und Filter.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        „? / 5“ heißt: die Installation läuft mit einer ProfiWare-Version vor 1.6.175 und meldet ihre Auslastung noch nicht.
      </Typography>

      <Bestaetigung
        offen={!!weg} titel="Installation aus der Liste entfernen?" knopf="Entfernen"
        text={`${weg?.kunde_name || weg?.firma || 'Diese Installation'} verschwindet aus der Liste. An der Lizenz ändert das nichts – meldet sich die Installation wieder, erscheint sie von selbst neu.`}
        onNein={() => setWeg(null)}
        onJa={async () => { await installationEntfernen(weg.id); setWeg(null); laden(); }}
      />
    </Box>
  );
}
